// Bloodwork Interpretation — read a graphical haematology + biochemistry report,
// flag each analyte High / Low / Normal against its reference interval, then
// interpret the pattern. Wrong answers reveal teaching text so the learner
// improves next time. Cert IV clinical-pathology interpretation.

export const statusOf = (a) => {
  if (a.value < a.low) return "low";
  if (a.value > a.high) return "high";
  return "normal";
};

// Beat 1 — flag classification (out of 40).
export const gradeFlags = (panel, selections) => {
  let correct = 0;
  panel.forEach((a, i) => {
    if (selections[i] === statusOf(a)) correct += 1;
  });
  return { correct, total: panel.length, pts: Math.round((40 * correct) / panel.length) };
};

export const optPts = (score, full, partial) => (score >= 3 ? full : score === 1 ? partial : 0);

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Prior-visit values (for the trend view) + next-test linkage, keyed by case id.
const PRIOR = {
  "ckd-cat": {
    values: { Creatinine: 250, Urea: 19.0, Phosphate: 2.2, "HCT (PCV)": 0.28, Potassium: 3.8 },
    next: "Confirm and stage with a urinalysis (USG, sediment, UPC ratio) and blood pressure — head to the Urinalysis bench next.",
    route: "/urinalysis", label: "Open the Urinalysis bench",
  },
  "haemolysis-dog": {
    values: { "HCT (PCV)": 0.46, Reticulocytes: 28, "Total bilirubin": 6, WBC: 11.0 },
    next: "Run a blood smear (spherocytes / agglutination) and a saline-agglutination test; image for an underlying trigger.",
    route: "/clinpath", label: "Open the Clinical Pathology lab",
  },
  "hepatopathy-dog": {
    values: { ALT: 120, ALP: 210, "Total bilirubin": 8, Albumin: 30, Urea: 4.5, Glucose: 4.5 },
    next: "Pre/post bile-acid stimulation test and abdominal ultrasound to characterise the liver — links to the imaging path.",
    route: "/sim/radiography", label: "Open Radiography (imaging)",
  },
  "inflammation-dog": {
    values: { WBC: 12.0, Neutrophils: 8.0, Globulin: 32, "HCT (PCV)": 0.40 },
    next: "Culture & sensitivity from the draining wound and image the limb to localise the source before antibiotics.",
    route: "/sim/radiography", label: "Open Radiography (image the limb)",
  },
};

export const serveCase = (c) => {
  const prior = PRIOR[c.id];
  return {
    ...c,
    nextStep: prior?.next,
    nextRoute: prior?.route,
    nextLabel: prior?.label,
    panel: c.panel.map((a) => (prior?.values?.[a.analyte] != null ? { ...a, prev: prior.values[a.analyte] } : a)),
    interpret: { ...c.interpret, options: shuffle(c.interpret.options) },
    action: { ...c.action, options: shuffle(c.action.options) },
  };
};

export const BLOOD_CASES = [
  {
    id: "ckd-cat",
    name: "Tigger",
    species: "cat",
    signalment: "14 yr · MN DSH · 3.6 kg",
    briefing:
      "Tigger has been drinking more, losing weight and is a bit picky with food. The vet has run an in-house haematology and biochemistry panel. Read the report.",
    panel: [
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.22, unit: "L/L", low: 0.30, high: 0.45,
        teachLow: "A low PCV is anaemia. In chronic kidney disease it's usually non-regenerative — failing kidneys make less erythropoietin, so the marrow isn't stimulated." },
      { group: "Haematology", analyte: "WBC", value: 9.5, unit: "×10⁹/L", low: 5.5, high: 19.5 },
      { group: "Biochemistry", analyte: "Urea", value: 28.0, unit: "mmol/L", low: 5.0, high: 11.0,
        teachHigh: "High urea = azotaemia. Combined with a high creatinine it points to reduced kidney function (rule out dehydration/pre-renal causes too)." },
      { group: "Biochemistry", analyte: "Creatinine", value: 380, unit: "µmol/L", low: 40, high: 177,
        teachHigh: "Creatinine rises as glomerular filtration falls — a marker of reduced kidney function (renal azotaemia)." },
      { group: "Biochemistry", analyte: "Phosphate", value: 3.2, unit: "mmol/L", low: 0.8, high: 1.9,
        teachHigh: "Hyperphosphataemia is common in CKD because diseased kidneys can't excrete phosphate." },
      { group: "Biochemistry", analyte: "Potassium", value: 3.1, unit: "mmol/L", low: 3.5, high: 5.5,
        teachLow: "Hypokalaemia is common in CKD cats from renal potassium loss and poor intake — it can cause weakness." },
      { group: "Biochemistry", analyte: "Total protein", value: 72, unit: "g/L", low: 60, high: 80 },
      { group: "Biochemistry", analyte: "ALT", value: 55, unit: "U/L", low: 10, high: 100 },
    ],
    interpret: {
      prompt: "Looking at the whole pattern, what is the most likely interpretation?",
      options: [
        { t: "Azotaemia (high urea & creatinine) with hyperphosphataemia, hypokalaemia and a non-regenerative anaemia — consistent with chronic kidney disease.", score: 3,
          note: "Correct — the renal pattern (↑urea, ↑creatinine, ↑phosphate, ↓K⁺, mild anaemia) fits CKD, especially in an older PU/PD cat." },
        { t: "Liver disease — the picture is driven by the liver.", score: 0,
          note: "Liver enzymes (ALT) are normal here; the abnormalities are renal, not hepatic." },
        { t: "An inflammatory/infectious process — driven by the white cell count.", score: 0,
          note: "The WBC is normal, so this isn't an inflammatory leukogram." },
        { t: "Everything is within normal limits — no action needed.", score: 0,
          note: "Several values are well outside their reference intervals — this is clearly abnormal." },
      ],
    },
    action: {
      prompt: "What's the appropriate next step?",
      options: [
        { t: "Flag the results to the vet and gather more data — check urine specific gravity / urinalysis and hydration status to confirm and stage the kidney disease.", score: 3,
          note: "USG and urinalysis help confirm renal disease and rule out a pre-renal (dehydration) component before staging/treatment." },
        { t: "Reassure the owner it's nothing and send them home.", score: 0,
          note: "Marked azotaemia must be escalated, not dismissed." },
        { t: "Recommend the cat starts a high-phosphate diet.", score: 0,
          note: "The opposite — CKD cats need phosphate restriction, not loading." },
      ],
    },
  },

  {
    id: "haemolysis-dog",
    name: "Bruno",
    species: "dog",
    signalment: "5 yr · MN Cocker Spaniel · 14 kg",
    briefing:
      "Bruno is suddenly lethargic, weak and his gums look pale and slightly yellow. The vet runs bloods urgently.",
    panel: [
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.17, unit: "L/L", low: 0.37, high: 0.55,
        teachLow: "A markedly low PCV is severe anaemia — too few red cells to carry oxygen, explaining the weakness and pale gums." },
      { group: "Haematology", analyte: "Reticulocytes", value: 185, unit: "×10⁹/L", low: 0, high: 60,
        teachHigh: "A high reticulocyte count means the marrow is regenerating — a regenerative anaemia, so the cause is blood loss or destruction (haemolysis), not marrow failure." },
      { group: "Haematology", analyte: "WBC", value: 23.0, unit: "×10⁹/L", low: 6.0, high: 17.0,
        teachHigh: "A raised white cell count often accompanies immune-mediated haemolysis (an inflammatory leukogram)." },
      { group: "Haematology", analyte: "Platelets", value: 240, unit: "×10⁹/L", low: 200, high: 500 },
      { group: "Biochemistry", analyte: "Total bilirubin", value: 38, unit: "µmol/L", low: 0, high: 10,
        teachHigh: "Hyperbilirubinaemia with a regenerative anaemia points to haemolysis — red cells are being broken down faster than the liver can clear the pigment." },
      { group: "Biochemistry", analyte: "Urea", value: 7.0, unit: "mmol/L", low: 3.0, high: 9.0 },
      { group: "Biochemistry", analyte: "ALT", value: 70, unit: "U/L", low: 10, high: 100 },
      { group: "Biochemistry", analyte: "Total protein", value: 68, unit: "g/L", low: 55, high: 75 },
    ],
    interpret: {
      prompt: "What does this pattern most likely represent?",
      options: [
        { t: "A regenerative anaemia (high reticulocytes) with hyperbilirubinaemia — consistent with haemolysis, e.g. immune-mediated haemolytic anaemia (IMHA).", score: 3,
          note: "Correct — regeneration + jaundice + anaemia is the classic haemolysis pattern; IMHA is a key differential." },
        { t: "Chronic kidney disease.", score: 0,
          note: "Urea is normal and there's no azotaemia — this isn't renal." },
        { t: "A non-regenerative anaemia from marrow failure.", score: 0,
          note: "The high reticulocytes show the marrow IS responding — it's regenerative." },
        { t: "Normal results for a dog.", score: 0,
          note: "Severe anaemia with high bilirubin is far from normal." },
      ],
    },
    action: {
      prompt: "How should the team respond?",
      options: [
        { t: "Treat as urgent — inform the vet immediately, prepare for a blood smear/further testing and possible transfusion and immunosuppressive therapy.", score: 3,
          note: "Severe regenerative/haemolytic anaemia is an emergency needing prompt diagnostics and supportive care." },
        { t: "Book a routine recheck in two weeks.", score: 0,
          note: "A PCV of 0.17 can't wait — this is an emergency." },
        { t: "Recommend an iron supplement and send home.", score: 0,
          note: "This is haemolysis, not iron deficiency — it needs urgent veterinary management." },
      ],
    },
  },

  {
    id: "hepatopathy-dog",
    name: "Molly",
    species: "dog",
    signalment: "9 yr · FN Labrador · 28 kg",
    briefing:
      "Molly has been off her food, occasionally vomiting and seems quieter than usual. The vet runs a biochemistry and haematology panel.",
    panel: [
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.42, unit: "L/L", low: 0.37, high: 0.55 },
      { group: "Biochemistry", analyte: "ALT", value: 480, unit: "U/L", low: 10, high: 100,
        teachHigh: "ALT is a hepatocellular enzyme — a marked rise indicates liver cell damage/leakage." },
      { group: "Biochemistry", analyte: "ALP", value: 760, unit: "U/L", low: 20, high: 150,
        teachHigh: "A high ALP suggests cholestasis or hepatobiliary/steroid influence — supportive of liver involvement alongside the ALT." },
      { group: "Biochemistry", analyte: "Total bilirubin", value: 30, unit: "µmol/L", low: 0, high: 10,
        teachHigh: "Hyperbilirubinaemia here reflects reduced hepatic processing/cholestasis (jaundice)." },
      { group: "Biochemistry", analyte: "Albumin", value: 21, unit: "g/L", low: 26, high: 40,
        teachLow: "The liver makes albumin — a low albumin can reflect reduced hepatic synthesis in liver dysfunction." },
      { group: "Biochemistry", analyte: "Urea", value: 2.0, unit: "mmol/L", low: 3.0, high: 9.0,
        teachLow: "A low urea can occur with liver dysfunction because the liver converts ammonia to urea less effectively." },
      { group: "Biochemistry", analyte: "Glucose", value: 3.0, unit: "mmol/L", low: 3.3, high: 6.5,
        teachLow: "Mild hypoglycaemia can accompany significant hepatic dysfunction as the liver's glucose regulation falters." },
    ],
    interpret: {
      prompt: "What is the most likely interpretation?",
      options: [
        { t: "Elevated liver enzymes (ALT & ALP) with hyperbilirubinaemia, low albumin and low urea — consistent with hepatic (liver) disease/dysfunction.", score: 3,
          note: "Correct — enzyme leakage + cholestasis + reduced synthetic function (↓albumin, ↓urea) together point to the liver." },
        { t: "Kidney disease.", score: 0,
          note: "Urea is low (not high) and there's no azotaemia — the pattern is hepatic, not renal." },
        { t: "Haemolytic anaemia.", score: 0,
          note: "The PCV is normal — there's no anaemia here." },
        { t: "Normal liver, just a stress response.", score: 0,
          note: "Enzymes in the hundreds with jaundice and low albumin are not a normal/stress picture." },
      ],
    },
    action: {
      prompt: "What's the sensible next step?",
      options: [
        { t: "Flag to the vet and prepare for further liver work-up — e.g. bile acid stimulation test and abdominal imaging to characterise the disease.", score: 3,
          note: "Bile acids assess function and imaging looks for structural causes — the logical next diagnostics." },
        { t: "Ignore the enzymes — they're 'just numbers'.", score: 0,
          note: "These are markedly abnormal and clinically significant." },
        { t: "Start a high-protein diet immediately without veterinary input.", score: 0,
          note: "Protein management in liver disease is nuanced and is a veterinary decision, not an automatic step." },
      ],
    },
  },

  {
    id: "inflammation-dog",
    name: "Rex",
    species: "dog",
    signalment: "4 yr · MN Staffy · 22 kg",
    briefing:
      "Rex has a fever, is lethargic and has a draining wound on his leg. The vet runs bloods to assess him.",
    panel: [
      { group: "Haematology", analyte: "WBC", value: 29.0, unit: "×10⁹/L", low: 6.0, high: 17.0,
        teachHigh: "A high total white cell count signals the body responding to inflammation or infection (a leukocytosis)." },
      { group: "Haematology", analyte: "Neutrophils", value: 25.0, unit: "×10⁹/L", low: 3.0, high: 11.5,
        teachHigh: "Neutrophilia (high neutrophils) is the hallmark of bacterial infection/inflammation; a 'left shift' (immature neutrophils) supports active infection." },
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.33, unit: "L/L", low: 0.37, high: 0.55,
        teachLow: "A mildly low PCV with chronic inflammation is often an 'anaemia of inflammatory disease' — usually mild and non-regenerative." },
      { group: "Haematology", analyte: "Platelets", value: 320, unit: "×10⁹/L", low: 200, high: 500 },
      { group: "Biochemistry", analyte: "Globulin", value: 50, unit: "g/L", low: 18, high: 37,
        teachHigh: "Hyperglobulinaemia reflects the immune/inflammatory response (antibodies, acute-phase proteins) in chronic infection/inflammation." },
      { group: "Biochemistry", analyte: "Albumin", value: 28, unit: "g/L", low: 26, high: 40 },
      { group: "Biochemistry", analyte: "Urea", value: 6.0, unit: "mmol/L", low: 3.0, high: 9.0 },
      { group: "Biochemistry", analyte: "ALT", value: 60, unit: "U/L", low: 10, high: 100 },
    ],
    interpret: {
      prompt: "What does the pattern most likely indicate?",
      options: [
        { t: "A marked neutrophilia (high WBC/neutrophils) with hyperglobulinaemia and a mild anaemia of inflammation — an inflammatory/infectious leukogram.", score: 3,
          note: "Correct — leukocytosis with neutrophilia and raised globulins fits infection/inflammation, matching the draining wound and fever." },
        { t: "Chronic kidney disease.", score: 0,
          note: "Urea is normal — there's no renal azotaemia here." },
        { t: "Liver failure.", score: 0,
          note: "ALT and albumin are essentially normal — the picture isn't hepatic." },
        { t: "A regenerative anaemia from haemolysis.", score: 0,
          note: "The anaemia is mild and the driver here is the inflammatory leukogram, not haemolysis." },
      ],
    },
    action: {
      prompt: "What's the appropriate response?",
      options: [
        { t: "Inform the vet and support finding/treating the source — the draining wound — e.g. wound care, sampling for culture and appropriate antibiotics as directed.", score: 3,
          note: "Identify and treat the source of infection; culture guides appropriate antibiotic therapy." },
        { t: "Conclude it's cancer and recommend euthanasia.", score: 0,
          note: "An inflammatory leukogram with an obvious wound points to infection — that's a huge, unjustified leap." },
        { t: "Do nothing; high white cells are always normal in dogs.", score: 0,
          note: "A WBC of 29 with neutrophilia is clearly abnormal and clinically relevant." },
      ],
    },
  },

  {
    id: "diabetes-dog",
    name: "Coco",
    species: "dog",
    signalment: "8 yr · FN Miniature Schnauzer · 9 kg",
    briefing:
      "Coco is drinking and urinating excessively, is ravenously hungry yet losing weight. The vet runs a haematology and biochemistry panel.",
    panel: [
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.46, unit: "L/L", low: 0.37, high: 0.55 },
      { group: "Haematology", analyte: "WBC", value: 11.5, unit: "×10⁹/L", low: 6.0, high: 17.0 },
      { group: "Biochemistry", analyte: "Glucose", value: 24.0, unit: "mmol/L", low: 3.3, high: 6.5,
        teachHigh: "Marked, persistent hyperglycaemia above the renal threshold (~10–12 mmol/L) causes glucosuria and the PU/PD — the hallmark of diabetes mellitus in a dog." },
      { group: "Biochemistry", analyte: "Cholesterol", value: 11.5, unit: "mmol/L", low: 3.0, high: 7.0,
        teachHigh: "Hypercholesterolaemia is common in diabetes (and other endocrinopathies) due to altered fat metabolism." },
      { group: "Biochemistry", analyte: "ALP", value: 320, unit: "U/L", low: 20, high: 150,
        teachHigh: "A modest ALP rise can accompany diabetes from hepatic lipid changes; interpret with the whole picture." },
      { group: "Biochemistry", analyte: "Urea", value: 6.0, unit: "mmol/L", low: 3.0, high: 9.0 },
      { group: "Biochemistry", analyte: "ALT", value: 90, unit: "U/L", low: 10, high: 100 },
    ],
    interpret: {
      prompt: "What is the most likely interpretation?",
      options: [
        { t: "Marked hyperglycaemia with hypercholesterolaemia — consistent with diabetes mellitus.", score: 3,
          note: "Correct — a glucose of 24 in a PU/PD, polyphagic dog losing weight is classic diabetes; in dogs stress hyperglycaemia rarely reaches this level." },
        { t: "Stress hyperglycaemia — ignore it.", score: 0,
          note: "Dogs rarely spike to 24 mmol/L from stress; with the clinical signs this is diabetes." },
        { t: "Chronic kidney disease.", score: 0,
          note: "Urea is normal — there's no azotaemia." },
        { t: "Normal results.", score: 0,
          note: "Glucose and cholesterol are clearly elevated." },
      ],
    },
    action: {
      prompt: "What's the appropriate next step?",
      options: [
        { t: "Flag to the vet and gather supporting data — confirm with a urinalysis (glucosuria ± ketones) and consider fructosamine.", score: 3,
          note: "Confirms persistent hyperglycaemia and screens for ketones/DKA before treatment." },
        { t: "Advise the owner to start insulin today.", score: 0,
          note: "Starting and dosing insulin is a veterinary decision after confirmation and assessment." },
        { t: "Recommend a high-sugar diet to keep her energy up.", score: 0,
          note: "The opposite of appropriate diabetic management." },
      ],
    },
  },

  {
    id: "addisons-dog",
    name: "Pepper",
    species: "dog",
    signalment: "3 yr · FN Standard Poodle · 24 kg",
    briefing:
      "Pepper has had waxing-waning vomiting and lethargy and collapsed today; she is bradycardic and weak. The vet runs urgent bloods and electrolytes.",
    panel: [
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.40, unit: "L/L", low: 0.37, high: 0.55 },
      { group: "Haematology", analyte: "Lymphocytes", value: 4.5, unit: "×10⁹/L", low: 1.0, high: 4.8,
        teachHigh: "A high-normal lymphocyte count (no stress lymphopenia) in a very sick dog is a clue to hypoadrenocorticism." },
      { group: "Haematology", analyte: "Eosinophils", value: 1.6, unit: "×10⁹/L", low: 0.1, high: 1.25,
        teachHigh: "Eosinophilia (rather than the expected stress eosinopenia) in a collapsed dog also points to Addison's." },
      { group: "Biochemistry", analyte: "Potassium", value: 8.0, unit: "mmol/L", low: 3.5, high: 5.5,
        teachHigh: "Severe hyperkalaemia is life-threatening (cardiac arrhythmia/bradycardia) and a hallmark of an Addisonian crisis." },
      { group: "Biochemistry", analyte: "Sodium", value: 130, unit: "mmol/L", low: 144, high: 160,
        teachLow: "Hyponatraemia with hyperkalaemia gives a low Na:K ratio — the classic electrolyte pattern of hypoadrenocorticism." },
      { group: "Biochemistry", analyte: "Urea", value: 24.0, unit: "mmol/L", low: 3.0, high: 9.0,
        teachHigh: "Azotaemia here is largely pre-renal from hypovolaemia/poor perfusion." },
      { group: "Biochemistry", analyte: "Glucose", value: 3.0, unit: "mmol/L", low: 3.3, high: 6.5,
        teachLow: "Mild hypoglycaemia can occur with cortisol deficiency in Addison's." },
    ],
    interpret: {
      prompt: "What is the most likely interpretation?",
      options: [
        { t: "Severe hyperkalaemia with hyponatraemia (low Na:K ratio), no stress leukogram and azotaemia — consistent with hypoadrenocorticism (Addison's).", score: 3,
          note: "Correct — the electrolyte pattern plus the absent stress leukogram in a collapsed young dog is classic Addison's." },
        { t: "Primary chronic kidney disease.", score: 0,
          note: "The hyponatraemia, low Na:K ratio and lack of a stress leukogram point to Addison's rather than primary renal disease." },
        { t: "Diabetes mellitus.", score: 0,
          note: "Glucose is low, not high." },
        { t: "Normal results.", score: 0,
          note: "Potassium and sodium are markedly abnormal." },
      ],
    },
    action: {
      prompt: "How should the team respond?",
      options: [
        { t: "Treat as an emergency — alert the vet immediately about the life-threatening hyperkalaemia, support circulation with IV fluids as directed, and prepare for an ACTH stimulation test.", score: 3,
          note: "Hyperkalaemia is an immediate cardiac risk; resuscitation and definitive testing follow." },
        { t: "Book a routine recheck next week.", score: 0,
          note: "A potassium of 8.0 with collapse cannot wait." },
        { t: "Assume it's all sample artefact and discard the result.", score: 0,
          note: "The clinical picture fits true hyperkalaemia — never dismiss it without a clean confirmatory sample." },
      ],
    },
  },

  {
    id: "cushings-dog",
    name: "Buster",
    species: "dog",
    signalment: "10 yr · MN Beagle · 16 kg",
    briefing:
      "Buster has a pot-bellied appearance, thinning coat, ravenous appetite and marked PU/PD. The vet runs a screening panel.",
    panel: [
      { group: "Haematology", analyte: "Neutrophils", value: 13.5, unit: "×10⁹/L", low: 3.0, high: 11.5,
        teachHigh: "A mature neutrophilia with lymphopenia is a 'stress' (steroid) leukogram — expected with excess cortisol." },
      { group: "Haematology", analyte: "Lymphocytes", value: 0.7, unit: "×10⁹/L", low: 1.0, high: 4.8,
        teachLow: "Lymphopenia is part of the steroid leukogram seen with hyperadrenocorticism." },
      { group: "Biochemistry", analyte: "ALP", value: 1500, unit: "U/L", low: 20, high: 150,
        teachHigh: "A markedly raised ALP (often a steroid-induced isoenzyme) is a typical, though non-specific, finding in canine Cushing's." },
      { group: "Biochemistry", analyte: "ALT", value: 180, unit: "U/L", low: 10, high: 100,
        teachHigh: "A mild ALT rise can accompany the hepatic changes of hyperadrenocorticism." },
      { group: "Biochemistry", analyte: "Cholesterol", value: 9.5, unit: "mmol/L", low: 3.0, high: 7.0,
        teachHigh: "Hypercholesterolaemia is common with excess cortisol." },
      { group: "Biochemistry", analyte: "Glucose", value: 6.8, unit: "mmol/L", low: 3.3, high: 6.5,
        teachHigh: "Mild hyperglycaemia can occur as cortisol antagonises insulin." },
    ],
    interpret: {
      prompt: "What is the most likely interpretation?",
      options: [
        { t: "A stress (steroid) leukogram with a markedly raised ALP, hypercholesterolaemia and mild hyperglycaemia — supportive of hyperadrenocorticism (Cushing's).", score: 3,
          note: "Correct — these changes plus the clinical signs warrant specific screening (ACTH stim / LDDST); ALP alone isn't diagnostic." },
        { t: "Primary liver failure.", score: 0,
          note: "Albumin/bilirubin/urea aren't shown as failing; the pattern fits cortisol excess, and Cushing's must be confirmed with dynamic testing." },
        { t: "Diabetes mellitus is confirmed by these results.", score: 0,
          note: "Glucose is only mildly raised — not diagnostic of diabetes here." },
        { t: "All results are normal.", score: 0,
          note: "ALP, cholesterol and the leukogram are clearly abnormal." },
      ],
    },
    action: {
      prompt: "What's the sensible next step?",
      options: [
        { t: "Flag to the vet and recommend specific screening tests (ACTH stimulation or low-dose dexamethasone suppression) — these enzymes alone don't confirm Cushing's.", score: 3,
          note: "A raised ALP is supportive but non-specific; dynamic testing confirms the diagnosis." },
        { t: "Diagnose Cushing's from the ALP and start treatment.", score: 0,
          note: "ALP is non-specific; never start trilostane without confirmatory testing." },
        { t: "Ignore the ALP — it's always high in older dogs.", score: 0,
          note: "An ALP of 1500 with these signs is clinically significant." },
      ],
    },
  },

  {
    id: "blood-loss-dog",
    name: "Scout",
    species: "dog",
    signalment: "6 yr · MN Greyhound · 30 kg",
    briefing:
      "Scout was hit by a car an hour ago. He is pale, tachycardic and weak with a distending abdomen. The vet runs an emergency PCV/TP and a quick panel.",
    panel: [
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.24, unit: "L/L", low: 0.37, high: 0.55,
        teachLow: "An acutely falling PCV after trauma signals significant blood loss; with acute loss the PCV may even lag behind the true deficit." },
      { group: "Haematology", analyte: "Reticulocytes", value: 30, unit: "×10⁹/L", low: 0, high: 60,
        teachLow: "Regeneration takes 3–5 days to appear, so an acute haemorrhage often looks 'non-regenerative' at first — interpret with the timeline." },
      { group: "Haematology", analyte: "Platelets", value: 180, unit: "×10⁹/L", low: 200, high: 500,
        teachLow: "Platelets can dip with consumption/loss during significant haemorrhage." },
      { group: "Biochemistry", analyte: "Total protein", value: 42, unit: "g/L", low: 55, high: 75,
        teachLow: "Total protein falls alongside the PCV in whole-blood loss — a useful clue that anaemia is due to haemorrhage rather than destruction." },
      { group: "Biochemistry", analyte: "Albumin", value: 20, unit: "g/L", low: 26, high: 40,
        teachLow: "Albumin drops with whole-blood loss." },
      { group: "Biochemistry", analyte: "Glucose", value: 7.5, unit: "mmol/L", low: 3.3, high: 6.5 },
    ],
    interpret: {
      prompt: "What does this pattern most likely represent?",
      options: [
        { t: "Acute blood loss (haemorrhage) — anaemia with a concurrently low total protein, in a trauma patient; regeneration hasn't appeared yet.", score: 3,
          note: "Correct — a falling PCV WITH a low total protein after trauma points to whole-blood loss; reticulocytosis takes days to develop." },
        { t: "Immune-mediated haemolytic anaemia.", score: 0,
          note: "Haemolysis usually keeps protein normal and (after a few days) shows regeneration; the low protein here fits loss." },
        { t: "Chronic kidney disease anaemia.", score: 0,
          note: "This is an acute trauma presentation, not chronic non-regenerative anaemia." },
        { t: "Normal results.", score: 0,
          note: "A PCV of 0.24 with low protein in a collapsing dog is far from normal." },
      ],
    },
    action: {
      prompt: "How should the team respond?",
      options: [
        { t: "Treat as an emergency — alert the vet immediately, support with IV fluids/oxygen, control haemorrhage and prepare for possible transfusion and imaging (e.g. abdominal FAST).", score: 3,
          note: "Acute haemorrhagic shock needs immediate resuscitation, source control and monitoring." },
        { t: "Start an iron supplement and discharge.", score: 0,
          note: "This is acute haemorrhage, not iron-deficiency — it needs emergency care." },
        { t: "Recheck the PCV next week.", score: 0,
          note: "An actively bleeding trauma patient cannot wait." },
      ],
    },
  },

  {
    id: "sepsis-dog",
    name: "Daisy",
    species: "dog",
    signalment: "6 yr · F entire Golden Retriever · 28 kg",
    briefing:
      "Daisy is lethargic with PU/PD and a vulval discharge about four weeks after her season. She is febrile. The vet runs bloods.",
    panel: [
      { group: "Haematology", analyte: "WBC", value: 41.0, unit: "×10⁹/L", low: 6.0, high: 17.0,
        teachHigh: "A marked leukocytosis indicates a vigorous inflammatory/septic response." },
      { group: "Haematology", analyte: "Neutrophils", value: 34.0, unit: "×10⁹/L", low: 3.0, high: 11.5,
        teachHigh: "Neutrophilia is the hallmark of bacterial inflammation." },
      { group: "Haematology", analyte: "Band neutrophils", value: 3.5, unit: "×10⁹/L", low: 0, high: 0.3,
        teachHigh: "A 'left shift' (immature band neutrophils) with toxic change indicates significant active infection/sepsis — here, likely pyometra." },
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.34, unit: "L/L", low: 0.37, high: 0.55,
        teachLow: "A mild anaemia of inflammatory disease is common in chronic/septic illness." },
      { group: "Biochemistry", analyte: "Globulin", value: 55, unit: "g/L", low: 18, high: 37,
        teachHigh: "Hyperglobulinaemia reflects the immune/inflammatory response." },
      { group: "Biochemistry", analyte: "Urea", value: 13.0, unit: "mmol/L", low: 3.0, high: 9.0,
        teachHigh: "Mild azotaemia can reflect dehydration and early renal effects of sepsis/endotoxaemia." },
      { group: "Biochemistry", analyte: "Albumin", value: 23, unit: "g/L", low: 26, high: 40,
        teachLow: "Albumin is a negative acute-phase protein and falls with significant inflammation." },
    ],
    interpret: {
      prompt: "What is the most likely interpretation?",
      options: [
        { t: "A marked neutrophilia with a left shift (band neutrophils) and hyperglobulinaemia — an inflammatory/septic leukogram; with the history, consistent with pyometra/sepsis.", score: 3,
          note: "Correct — left shift + the post-season vulval discharge strongly suggest pyometra, a surgical emergency." },
        { t: "A stress leukogram.", score: 0,
          note: "Stress doesn't produce a left shift with band neutrophils." },
        { t: "Leukaemia.", score: 0,
          note: "This is a reactive, inflammatory pattern with an obvious clinical source." },
        { t: "Normal results.", score: 0,
          note: "A WBC of 41 with a left shift is markedly abnormal." },
      ],
    },
    action: {
      prompt: "How should the team respond?",
      options: [
        { t: "Treat as urgent — alert the vet promptly (likely pyometra/sepsis), support with IV fluids, and prepare for imaging and surgery.", score: 3,
          note: "Pyometra is a surgical emergency; stabilise and prepare for ovariohysterectomy." },
        { t: "Send home with oral antibiotics and recheck in a week.", score: 0,
          note: "A septic patient needs hospitalisation and usually surgery, not delayed outpatient care." },
        { t: "Reassure the owner it's a minor infection.", score: 0,
          note: "This is potentially life-threatening and must be escalated." },
      ],
    },
  },

  {
    id: "hypothyroid-dog",
    name: "Bear",
    species: "dog",
    signalment: "7 yr · MN Golden Retriever · 34 kg",
    briefing:
      "Bear has gained weight despite no diet change, is lethargic, seeks heat and has a dull, thinning coat. The vet runs a screening panel.",
    panel: [
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.33, unit: "L/L", low: 0.37, high: 0.55,
        teachLow: "A mild non-regenerative anaemia can accompany hypothyroidism (reduced metabolic drive on the marrow)." },
      { group: "Haematology", analyte: "WBC", value: 9.0, unit: "×10⁹/L", low: 6.0, high: 17.0 },
      { group: "Biochemistry", analyte: "Cholesterol", value: 13.0, unit: "mmol/L", low: 3.0, high: 7.0,
        teachHigh: "Marked hypercholesterolaemia is a classic supportive finding in canine hypothyroidism." },
      { group: "Biochemistry", analyte: "Triglycerides", value: 4.5, unit: "mmol/L", low: 0.2, high: 1.5,
        teachHigh: "Hypertriglyceridaemia also reflects the slowed lipid metabolism of hypothyroidism." },
      { group: "Biochemistry", analyte: "ALT", value: 95, unit: "U/L", low: 10, high: 100 },
      { group: "Biochemistry", analyte: "Glucose", value: 4.8, unit: "mmol/L", low: 3.3, high: 6.5 },
    ],
    interpret: {
      prompt: "What is the most likely interpretation?",
      options: [
        { t: "A mild non-regenerative anaemia with marked hypercholesterolaemia — supportive of hypothyroidism; confirm with thyroid testing.", score: 3,
          note: "Correct — the lipid changes plus the clinical signs warrant thyroid function testing (total/free T4, TSH) to confirm." },
        { t: "Diabetes mellitus.", score: 0,
          note: "Glucose is normal — there's no hyperglycaemia." },
        { t: "Liver failure.", score: 0,
          note: "ALT is normal and there's no synthetic-failure pattern." },
        { t: "All results are normal.", score: 0,
          note: "Cholesterol and triglycerides are clearly elevated with a mild anaemia." },
      ],
    },
    action: {
      prompt: "What's the sensible next step?",
      options: [
        { t: "Flag to the vet and recommend thyroid function testing (total T4 ± free T4 and TSH) to confirm before treatment.", score: 3,
          note: "Lipid changes are supportive but not diagnostic — thyroid testing confirms hypothyroidism." },
        { t: "Start thyroid medication based on the cholesterol alone.", score: 0,
          note: "Never start levothyroxine without confirmatory thyroid testing." },
        { t: "Recommend a high-fat diet.", score: 0,
          note: "Inappropriate — these dogs already have lipid abnormalities." },
      ],
    },
  },

  {
    id: "hyperthyroid-cat",
    name: "Smokey",
    species: "cat",
    signalment: "14 yr · MN DSH · 3.2 kg",
    briefing:
      "Smokey is losing weight despite a huge appetite, is hyperactive and vocal, and has a fast heart rate. The vet runs bloods including a thyroid hormone.",
    panel: [
      { group: "Biochemistry", analyte: "Total T4", value: 110, unit: "nmol/L", low: 15, high: 50,
        teachHigh: "A markedly elevated total T4 confirms hyperthyroidism in a cat with supportive clinical signs." },
      { group: "Biochemistry", analyte: "ALT", value: 180, unit: "U/L", low: 10, high: 100,
        teachHigh: "Mild–moderate liver enzyme elevations (ALT/ALP) are common in hyperthyroidism and usually resolve with treatment." },
      { group: "Biochemistry", analyte: "ALP", value: 130, unit: "U/L", low: 14, high: 111,
        teachHigh: "A modest ALP rise is typical of the high metabolic rate of hyperthyroidism." },
      { group: "Biochemistry", analyte: "Urea", value: 9.5, unit: "mmol/L", low: 5.0, high: 11.0 },
      { group: "Biochemistry", analyte: "Creatinine", value: 110, unit: "µmol/L", low: 70, high: 180,
        teachLow: "Creatinine may look 'good' because the high metabolic rate increases kidney blood flow — masking concurrent CKD until the thyroid is treated." },
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.46, unit: "L/L", low: 0.30, high: 0.45,
        teachHigh: "A mildly high PCV can occur with the increased metabolic/erythropoietic drive of hyperthyroidism." },
    ],
    interpret: {
      prompt: "What is the most likely interpretation?",
      options: [
        { t: "A markedly elevated total T4 with mild liver enzyme rises — consistent with hyperthyroidism in an older cat.", score: 3,
          note: "Correct — high T4 plus weight loss, polyphagia and tachycardia is classic feline hyperthyroidism; the liver enzymes usually settle with treatment." },
        { t: "Primary liver disease driving everything.", score: 0,
          note: "The high T4 explains the mild enzyme rises; the primary problem is thyroidal." },
        { t: "Chronic kidney disease — the kidneys are clearly failing.", score: 1,
          note: "Creatinine looks normal now, but hyperthyroidism can mask CKD — recheck renal values once euthyroid." },
        { t: "Normal results for a senior cat.", score: 0,
          note: "A T4 of 110 is well above range." },
      ],
    },
    action: {
      prompt: "What's the appropriate next step?",
      options: [
        { t: "Flag to the vet to confirm and discuss treatment options, and warn that kidney values should be rechecked once the cat is euthyroid (hyperthyroidism can mask CKD).", score: 3,
          note: "Treat the thyroid, then reassess renal function — a key nursing/clinical caveat in older cats." },
        { t: "Reassure the owner it's just old age.", score: 0,
          note: "These are treatable disease findings, not normal ageing." },
        { t: "Recommend a calorie-restricted diet for the weight loss.", score: 0,
          note: "The weight loss is from hyperthyroidism — treat the cause, don't restrict calories." },
      ],
    },
  },

  {
    id: "hypercalcaemia-dog",
    name: "Zeus",
    species: "dog",
    signalment: "9 yr · MN Boxer · 32 kg",
    briefing:
      "Zeus has PU/PD, reduced appetite and lethargy. On examination the vet finds enlarged peripheral lymph nodes and runs a panel.",
    panel: [
      { group: "Biochemistry", analyte: "Calcium (total)", value: 4.1, unit: "mmol/L", low: 2.3, high: 3.0,
        teachHigh: "Marked hypercalcaemia is a 'red flag' — a leading cause in dogs is malignancy (e.g. lymphoma, anal-sac carcinoma), alongside primary hyperparathyroidism and others." },
      { group: "Biochemistry", analyte: "Phosphate", value: 1.0, unit: "mmol/L", low: 0.8, high: 1.6 },
      { group: "Biochemistry", analyte: "Urea", value: 12.0, unit: "mmol/L", low: 3.0, high: 9.0,
        teachHigh: "Persistent hypercalcaemia can impair urine concentration and cause renal injury, producing PU/PD and azotaemia." },
      { group: "Biochemistry", analyte: "Creatinine", value: 200, unit: "µmol/L", low: 40, high: 130,
        teachHigh: "Azotaemia may reflect hypercalcaemic nephropathy/dehydration." },
      { group: "Haematology", analyte: "Lymphocytes", value: 9.0, unit: "×10⁹/L", low: 1.0, high: 4.8,
        teachHigh: "A lymphocytosis with enlarged nodes and hypercalcaemia raises strong suspicion of lymphoma." },
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.38, unit: "L/L", low: 0.37, high: 0.55 },
    ],
    interpret: {
      prompt: "What is the most likely interpretation?",
      options: [
        { t: "Marked hypercalcaemia with a lymphocytosis and lymphadenopathy — concerning for malignancy (e.g. lymphoma); secondary azotaemia from hypercalcaemic effects on the kidney.", score: 3,
          note: "Correct — hypercalcaemia of malignancy is a leading differential and must be investigated promptly." },
        { t: "Primary kidney disease causing the high calcium.", score: 1,
          note: "CKD more often LOWERS or keeps calcium normal; the marked hypercalcaemia with nodes points to malignancy — investigate first." },
        { t: "Diabetes mellitus.", score: 0,
          note: "Glucose isn't the abnormality; calcium is." },
        { t: "Normal results.", score: 0,
          note: "A calcium of 4.1 mmol/L is markedly abnormal." },
      ],
    },
    action: {
      prompt: "What's the appropriate next step?",
      options: [
        { t: "Flag to the vet urgently — recommend investigating the cause (e.g. node aspirates, ionised calcium/PTH-related work-up, imaging) and supporting the patient with IV fluids as directed.", score: 3,
          note: "Hypercalcaemia is significant; find the cause and protect the kidneys with fluid therapy." },
        { t: "Add a calcium supplement to help him feel better.", score: 0,
          note: "He already has too much calcium — supplementing would be dangerous." },
        { t: "Recheck in a month.", score: 0,
          note: "Marked hypercalcaemia with azotaemia needs prompt investigation." },
      ],
    },
  },

  {
    id: "pancreatitis-dog",
    name: "Pudding",
    species: "dog",
    signalment: "8 yr · FN Miniature Schnauzer · 11 kg",
    briefing:
      "Pudding raided the bin two days ago and now has repeated vomiting, a painful abdomen and is off her food. The vet runs bloods and a specific pancreatic test.",
    panel: [
      { group: "Haematology", analyte: "WBC", value: 23.0, unit: "×10⁹/L", low: 6.0, high: 17.0,
        teachHigh: "An inflammatory leukocytosis is common with acute pancreatitis." },
      { group: "Haematology", analyte: "Neutrophils", value: 19.0, unit: "×10⁹/L", low: 3.0, high: 11.5,
        teachHigh: "Neutrophilia reflects the acute inflammatory process." },
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.56, unit: "L/L", low: 0.37, high: 0.55,
        teachHigh: "A high PCV here reflects haemoconcentration from dehydration (vomiting)." },
      { group: "Biochemistry", analyte: "cPLI (spec cPL)", value: 600, unit: "µg/L", low: 0, high: 200,
        teachHigh: "A markedly raised specific canine pancreatic lipase strongly supports pancreatitis in the right clinical context." },
      { group: "Biochemistry", analyte: "ALT", value: 160, unit: "U/L", low: 10, high: 100,
        teachHigh: "Reactive liver enzyme rises are common given the pancreas's proximity to the liver." },
      { group: "Biochemistry", analyte: "Glucose", value: 7.2, unit: "mmol/L", low: 3.3, high: 6.5 },
    ],
    interpret: {
      prompt: "What is the most likely interpretation?",
      options: [
        { t: "An inflammatory leukogram with haemoconcentration and a markedly raised pancreatic lipase — consistent with acute pancreatitis.", score: 3,
          note: "Correct — the dietary indiscretion, vomiting, abdominal pain and raised cPL fit acute pancreatitis." },
        { t: "Chronic kidney disease.", score: 0,
          note: "There's no azotaemia and the high PCV reflects dehydration, not renal disease." },
        { t: "Diabetes mellitus.", score: 0,
          note: "Glucose is only mildly raised (stress/illness), not diabetic." },
        { t: "Normal results.", score: 0,
          note: "The leukogram and lipase are clearly abnormal." },
      ],
    },
    action: {
      prompt: "What's the appropriate next step?",
      options: [
        { t: "Flag to the vet and support the patient — IV fluids for the dehydration, anti-emesis/analgesia and nutritional support as directed.", score: 3,
          note: "Pancreatitis management is supportive: fluids, pain relief, anti-emetics and early feeding where tolerated." },
        { t: "Withhold all fluids to 'rest' the pancreas.", score: 0,
          note: "Outdated and harmful — these dehydrated patients need IV fluid support." },
        { t: "Send home immediately on a fatty diet.", score: 0,
          note: "A high-fat diet can worsen pancreatitis; a low-fat approach is advised." },
      ],
    },
  },

  {
    id: "anaemia-chronic-cat",
    name: "Pixie",
    species: "cat",
    signalment: "10 yr · FN DSH · 4.0 kg",
    briefing:
      "Pixie has been quiet and inappetent for a few weeks with mildly pale gums. The vet runs a haematology and biochemistry panel.",
    panel: [
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.21, unit: "L/L", low: 0.30, high: 0.45,
        teachLow: "A low PCV is anaemia; the cause is defined by whether the marrow is responding (regenerative) or not (non-regenerative)." },
      { group: "Haematology", analyte: "Reticulocytes", value: 10, unit: "×10⁹/L", low: 0, high: 60,
        teachLow: "A low/normal reticulocyte count with anaemia indicates a NON-regenerative process (marrow not responding)." },
      { group: "Haematology", analyte: "WBC", value: 8.0, unit: "×10⁹/L", low: 5.5, high: 19.5 },
      { group: "Biochemistry", analyte: "Globulin", value: 60, unit: "g/L", low: 28, high: 51,
        teachHigh: "Hyperglobulinaemia suggests chronic antigenic/inflammatory stimulation — investigate chronic infection/inflammatory disease." },
      { group: "Biochemistry", analyte: "Total protein", value: 90, unit: "g/L", low: 60, high: 80,
        teachHigh: "The high total protein here is driven by the raised globulin." },
      { group: "Biochemistry", analyte: "Urea", value: 9.0, unit: "mmol/L", low: 5.0, high: 11.0 },
    ],
    interpret: {
      prompt: "What does this pattern most likely represent?",
      options: [
        { t: "A non-regenerative anaemia with hyperglobulinaemia — an anaemia of chronic (inflammatory) disease; investigate an underlying chronic illness.", score: 3,
          note: "Correct — low reticulocytes + raised globulins point to chronic disease; pursue the underlying cause." },
        { t: "A regenerative anaemia from acute blood loss.", score: 0,
          note: "Reticulocytes are low, so it is non-regenerative." },
        { t: "Chronic kidney disease anaemia is confirmed.", score: 1,
          note: "Possible contributor, but urea is normal and the hyperglobulinaemia points to chronic inflammation — investigate further." },
        { t: "Normal results.", score: 0,
          note: "A PCV of 0.21 with high globulin is abnormal." },
      ],
    },
    action: {
      prompt: "What's the appropriate next step?",
      options: [
        { t: "Flag to the vet and recommend investigating the underlying chronic disease (e.g. infection, inflammation, neoplasia) — a blood smear and further work-up.", score: 3,
          note: "Treating the underlying cause is what improves an anaemia of chronic disease." },
        { t: "Give an iron injection and discharge.", score: 0,
          note: "This isn't iron-deficiency anaemia; the cause must be found." },
        { t: "Ignore it — older cats are always a bit anaemic.", score: 0,
          note: "A PCV of 0.21 with hyperglobulinaemia is clinically significant." },
      ],
    },
  },

  {
    id: "thrombocytopenia-dog",
    name: "Rolo",
    species: "dog",
    signalment: "5 yr · FN Cavalier King Charles Spaniel · 8 kg",
    briefing:
      "Rolo has pinpoint bruises on the belly and gums (petechiae) and a recent nosebleed. The vet runs a haematology panel.",
    panel: [
      { group: "Haematology", analyte: "Platelets", value: 18, unit: "×10⁹/L", low: 200, high: 500,
        teachLow: "A severe thrombocytopenia (very low platelets) causes spontaneous bleeding/petechiae and is a 'red flag' result — but always rule out clumping/clotted samples first." },
      { group: "Haematology", analyte: "HCT (PCV)", value: 0.32, unit: "L/L", low: 0.37, high: 0.55,
        teachLow: "A mild anaemia can follow blood loss from the bleeding tendency." },
      { group: "Haematology", analyte: "WBC", value: 11.0, unit: "×10⁹/L", low: 6.0, high: 17.0 },
      { group: "Biochemistry", analyte: "Albumin", value: 30, unit: "g/L", low: 26, high: 40 },
      { group: "Biochemistry", analyte: "Urea", value: 6.0, unit: "mmol/L", low: 3.0, high: 9.0 },
    ],
    interpret: {
      prompt: "What is the most likely interpretation (after excluding a clumped/clotted sample)?",
      options: [
        { t: "Severe thrombocytopenia causing a bleeding tendency (petechiae, epistaxis) — e.g. immune-mediated thrombocytopenia; confirm on a fresh smear.", score: 3,
          note: "Correct — petechiae with a platelet count of 18 fits severe thrombocytopenia; always verify the count on a smear (rule out clumping) before acting." },
        { t: "Normal platelets — the analyser is reliable, no need to check a smear.", score: 0,
          note: "An automated low platelet count MUST be confirmed on a smear (EDTA clumping is a common artefact)." },
        { t: "A regenerative anaemia from haemolysis.", score: 0,
          note: "The standout abnormality is the platelet count, not haemolysis." },
        { t: "Liver failure.", score: 0,
          note: "Albumin/urea are normal; this is a platelet problem." },
      ],
    },
    action: {
      prompt: "What's the appropriate next step?",
      options: [
        { t: "Confirm the count on a fresh blood smear (estimate platelets, check for clumping), then flag urgently to the vet — handle gently to avoid provoking bleeding.", score: 3,
          note: "Smear confirmation prevents acting on an artefact; severe true thrombocytopenia is an emergency and the patient must be handled carefully." },
        { t: "Take a jugular sample for more tests straight away.", score: 0,
          note: "Avoid large-vessel/jugular venepuncture in a severely thrombocytopenic patient — bleeding risk; use a peripheral vein with care." },
        { t: "Send home and recheck in two weeks.", score: 0,
          note: "Spontaneous bleeding with a platelet count of 18 needs urgent attention." },
      ],
    },
  },

  {
    id: "ethylene-glycol-cat",
    name: "Sooty",
    species: "cat",
    signalment: "2 yr · MN DSH · 4.2 kg",
    briefing:
      "Sooty was found wobbly in the garage and is now depressed and dehydrated. The owner has antifreeze stored there. The vet runs urgent bloods.",
    panel: [
      { group: "Biochemistry", analyte: "Urea", value: 35.0, unit: "mmol/L", low: 5.0, high: 11.0,
        teachHigh: "Severe azotaemia develops as ethylene-glycol metabolites cause acute kidney injury." },
      { group: "Biochemistry", analyte: "Creatinine", value: 600, unit: "µmol/L", low: 70, high: 180,
        teachHigh: "A markedly raised creatinine reflects acute renal failure — a hallmark of ethylene-glycol (antifreeze) toxicity." },
      { group: "Biochemistry", analyte: "Potassium", value: 7.2, unit: "mmol/L", low: 3.5, high: 5.5,
        teachHigh: "Hyperkalaemia accompanies acute kidney injury/anuria and is life-threatening (cardiac risk)." },
      { group: "Biochemistry", analyte: "Calcium (total)", value: 1.6, unit: "mmol/L", low: 2.0, high: 2.8,
        teachLow: "Hypocalcaemia is characteristic — oxalate (a glycol metabolite) binds calcium, forming calcium-oxalate crystals." },
      { group: "Biochemistry", analyte: "Glucose", value: 9.0, unit: "mmol/L", low: 3.3, high: 6.5,
        teachHigh: "Mild hyperglycaemia can occur with ethylene-glycol toxicity and the stress of severe illness." },
      { group: "Haematology", analyte: "PCV", value: 0.55, unit: "L/L", low: 0.30, high: 0.45,
        teachHigh: "A high PCV here reflects haemoconcentration from dehydration." },
    ],
    interpret: {
      prompt: "Given the history and pattern, what is the most likely interpretation?",
      options: [
        { t: "Severe azotaemia with hyperkalaemia and hypocalcaemia — acute kidney injury consistent with ethylene-glycol (antifreeze) toxicity.", score: 3,
          note: "Correct — acute renal failure with the classic low calcium (oxalate binding) and the garage/antifreeze history points strongly to ethylene-glycol poisoning." },
        { t: "Chronic kidney disease.", score: 0,
          note: "This is acute (a young cat, sudden onset, antifreeze access) — not chronic; and CKD doesn't typically cause this marked hypocalcaemia." },
        { t: "Diabetes mellitus.", score: 0,
          note: "The glucose is only mildly raised; the dominant problem is acute renal failure." },
        { t: "Normal results.", score: 0,
          note: "Severe azotaemia with hyperkalaemia and hypocalcaemia is far from normal." },
      ],
    },
    action: {
      prompt: "How should the team respond?",
      options: [
        { t: "Treat as a critical emergency — alert the vet immediately (life-threatening hyperkalaemia and acute renal failure), support with IV fluids, and recognise that antidotal treatment is only useful very early.", score: 3,
          note: "Ethylene-glycol toxicity is an emergency; the antidote works only if given early, and the hyperkalaemia is an immediate cardiac threat." },
        { t: "Book a routine recheck next week.", score: 0,
          note: "Acute renal failure with hyperkalaemia cannot wait." },
        { t: "Reassure the owner it's a mild upset and send home.", score: 0,
          note: "This is potentially fatal and must be escalated immediately." },
      ],
    },
  },
];
